<!--
  Snowflake connection status indicator.

  Lives in the top header bar (default layout). A small colored dot +
  the word "Status" — green when we can talk to Snowflake, red when we
  can't, amber while the first probe is in flight. Hovering reveals a
  card with the user's live session details (CURRENT_USER, ROLE,
  WAREHOUSE, DB.SCHEMA, ACCOUNT, REGION, VERSION, SESSION).

  Why an actual query rather than a ping:
    - The dot needs to mean "Snowflake will accept queries from this
      session right now." A 200 from Snowflake's REST endpoint without
      a real query doesn't prove the access token + role + warehouse
      assignment are healthy; a SELECT CURRENT_*() does.
    - The session details are the most useful "what's even going on"
      surface for an operator — surfacing them on hover is essentially
      free given we already had to query for the health check.

  Refresh cadence:
    - Initial probe on mount.
    - Re-probe on hover (so an operator who's worried about state can
      force a fresh check by mousing in).
    - Background re-probe every 60s while the layout is mounted.
    - Clicking the chip also forces an immediate re-check.

  Failure modes the chip surfaces:
    - No Snowflake OAuth token (user signed in via password / keypair /
      PAT — we can't query from the browser without the OAuth flow yet).
      Surfaced as "Disconnected" with a clear "OAuth not configured"
      message in the hover card.
    - Token expired and refresh failed → snowflakeAuth() throws; the
      catch falls through to "Disconnected" with the connector's
      message, plus a hint to re-login.
    - SQL error from Snowflake → surfaced verbatim.
-->

<template>
  <div
    class="relative"
    @mouseenter="onHover"
    @mouseleave="hovered = false"
  >
    <button
      type="button"
      class="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      :title="`Snowflake: ${displayLabel}`"
      @click="probe"
    >
      <span class="relative flex h-2.5 w-2.5">
        <!-- Outer pulse — only when connected, gives a "live" feel -->
        <span
          v-if="status === 'connected'"
          class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60"
        />
        <span class="relative inline-flex h-2.5 w-2.5 rounded-full" :class="dotClass" />
      </span>
      <span class="text-gray-700 dark:text-gray-200">Status</span>
    </button>

    <!-- Hover card. Anchored to the chip's right edge so it doesn't
         spill off the viewport on a narrow header. -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="hovered"
        class="absolute right-0 top-full z-50 mt-2 w-[22rem] rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1E2130] shadow-xl ring-1 ring-black/5"
      >
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-gray-200 dark:border-white/10 px-4 py-2.5">
          <div class="flex items-center gap-2">
            <span class="relative flex h-2 w-2">
              <span class="relative inline-flex h-2 w-2 rounded-full" :class="dotClass" />
            </span>
            <h4 class="text-sm font-semibold" :class="labelTextClass">
              {{ displayLabel }}
            </h4>
          </div>
          <span v-if="lastCheckedAt" class="text-[10px] text-gray-400 dark:text-gray-500">
            {{ formatRelative(lastCheckedAt) }}
          </span>
        </div>

        <!-- Body — diverges by status -->
        <div class="px-4 py-3 text-xs">
          <!-- Connected: render the session detail grid -->
          <dl
            v-if="status === 'connected' && details"
            class="grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-1.5"
          >
            <dt class="text-gray-500 dark:text-gray-400">User</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.user || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Role</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.role || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Warehouse</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.warehouse || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Database</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.database || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Schema</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.schema || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Account</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.account || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Identifier</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ accountIdentifier || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Region</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.region || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Version</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.version || '—' }}</dd>

            <dt class="text-gray-500 dark:text-gray-400">Session</dt>
            <dd class="truncate font-mono text-gray-900 dark:text-gray-100">{{ details.sessionId || '—' }}</dd>
          </dl>

          <!-- Checking — initial probe in flight -->
          <p v-else-if="status === 'checking'" class="text-gray-500 dark:text-gray-400 italic">
            Querying Snowflake for session details…
          </p>

          <!-- Disconnected — show the error verbatim, the user is the
               operator and benefits from the raw Snowflake message.
               No "OAuth nudge" here anymore — the chip now successfully
               probes PAT / password / keypair connections via the
               backend, so a disconnect at this point means the actual
               connection (whatever auth method) failed. -->
          <p v-else class="text-red-600 dark:text-red-400 leading-snug">
            {{ errorMessage || 'No active Snowflake connection.' }}
          </p>
        </div>

        <!-- Footer — manual refresh -->
        <div class="flex items-center justify-end border-t border-gray-200 dark:border-white/10 px-3 py-2">
          <button
            type="button"
            class="text-[11px] font-medium text-purple-600 dark:text-purple-400 hover:underline disabled:opacity-50 disabled:no-underline"
            :disabled="status === 'checking'"
            @click.stop="probe"
          >
            {{ status === 'checking' ? 'Checking…' : 'Refresh' }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

type Status = 'checking' | 'connected' | 'disconnected'

interface SessionDetails {
  user: string
  role: string
  warehouse: string
  database: string
  schema: string
  region: string
  account: string
  version: string
  sessionId: string
}

// Single SELECT — cheap, no warehouse compute, returns one row of
// CURRENT_*() values. Quoted aliases keep the original case so the
// response parser produces { user, role, ... } verbatim.
// Picks up CURRENT_AVAILABLE_ROLES() too so the role switcher in the
// header has a fresh list every probe. Returned as a JSON array
// string we parse on receipt.
const STATUS_QUERY = `
  SELECT
    CURRENT_USER()             AS "user",
    CURRENT_ROLE()             AS "role",
    CURRENT_WAREHOUSE()        AS "warehouse",
    CURRENT_DATABASE()         AS "database",
    CURRENT_SCHEMA()           AS "schema",
    CURRENT_REGION()           AS "region",
    CURRENT_ACCOUNT()          AS "account",
    CURRENT_VERSION()          AS "version",
    CURRENT_SESSION()          AS "sessionId",
    CURRENT_AVAILABLE_ROLES()  AS "availableRoles"
`

const REFRESH_INTERVAL_MS = 60_000

const status = ref<Status>('checking')
const details = ref<SessionDetails | null>(null)
const errorMessage = ref<string | null>(null)
const lastCheckedAt = ref<Date | null>(null)
const hovered = ref(false)
const hasOAuthToken = ref(false)

// The Snowflake account *identifier* (org-name/account-name, e.g.
// EEDEPYG-BJ42399) — NOT the same as CURRENT_ACCOUNT() which returns
// the numeric account name. The identifier is what a user types to
// reach their account; it lives on the account doc rather than in
// the session probe response, so we surface it from the globals
// store independent of which probe path served the rest of the
// session details.
const { accountProfile } = storeToRefs(useGlobalsStore())
const accountIdentifier = computed(() => {
  return (accountProfile.value as any)?.snowflakeSettings?.sfId || ''
})

let intervalId: ReturnType<typeof setInterval> | null = null
let inflight = false

const dotClass = computed(() => {
  switch (status.value) {
    case 'connected': return 'bg-green-500'
    case 'disconnected': return 'bg-red-500'
    default: return 'bg-amber-400'
  }
})

const labelTextClass = computed(() => {
  switch (status.value) {
    case 'connected': return 'text-green-700 dark:text-green-300'
    case 'disconnected': return 'text-red-700 dark:text-red-300'
    default: return 'text-amber-700 dark:text-amber-300'
  }
})

const displayLabel = computed(() => {
  switch (status.value) {
    case 'connected': return 'Connected'
    case 'disconnected': return 'Disconnected'
    default: return 'Checking…'
  }
})

/**
 * PASSIVELY look for a usable Snowflake OAuth session.
 *
 * Two storage layers because applicationAuth.userLogin() mirrors the
 * snowflakeAuth payload to BOTH:
 *   1. cookie  (`snowflakeAuth`) — the canonical store, used by
 *      useSnowflakeAuthStore() for refresh/auth flows.
 *   2. localStorage `snowflakeAuth` — JSON-serialized backup. Important
 *      because the OAuth payload (encrypted refresh token + client
 *      secret + everything else) regularly exceeds the 4 KB browser
 *      cookie size cap, in which case the cookie is silently dropped
 *      while localStorage still has the data.
 *
 * We MUST NOT call `useSnowflakeAuthStore().snowflakeAuth(...)` here.
 * That function isn't a probe — it's "open a session and force a
 * logout if it can't." On a Firebase email/password login it would
 * call `router.push('/logout')`, kicking the user back to /login
 * mid-session. The StatusIndicator must NEVER cause a logout side
 * effect — it's surveillance, not auth control.
 */
function peekOAuthSession(): { accessToken?: string, accessTokenExpiresAt?: number, refreshTokenExpiresAt?: number } {
  // Read both stores up front so we can pick whichever is complete.
  const cookie = useCookie<any>('snowflakeAuth')
  const cookieData = (cookie.value && typeof cookie.value === 'object') ? cookie.value : null

  let lsData: any = null
  if (import.meta.client) {
    try {
      const raw = localStorage.getItem('snowflakeAuth')
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object') lsData = parsed
      }
    } catch { /* fall through */ }
  }

  // "Complete" = has the fields useSnowflake() needs to either query
  // directly or refresh: accessToken + accessTokenExpiresAt + the
  // refresh hand-off fields. A partial cookie (oversized → browser
  // dropped some fields) returns false here.
  const isComplete = (d: any) =>
    !!(d && d.accessToken && d.accessTokenExpiresAt && d.refreshTokenExpiresAt)

  // Prefer cookie when complete; fall back to localStorage when the
  // cookie was truncated. Snowflake refresh tokens are large JWTs
  // and the full credentials payload regularly exceeds the 4 KB
  // browser cookie cap, in which case the cookie gets silently
  // dropped while localStorage still has the data.
  let v: any = isComplete(cookieData) ? cookieData : (isComplete(lsData) ? lsData : null)

  // If localStorage has the full payload but the cookie doesn't,
  // restore the cookie from localStorage. useSnowflake() still
  // reads via useSnowflakeAuthStore() which goes through the
  // cookie — without this restore, my peek would say "has OAuth"
  // but the actual query call would fall through to refresh-fail
  // and bounce the user to /logout.
  if (lsData && !isComplete(cookieData) && isComplete(lsData)) {
    try { cookie.value = lsData } catch (e) { console.warn('Could not restore cookie from localStorage:', e) }
  }

  if (!v || typeof v !== 'object') return {}
  return {
    accessToken: v.accessToken,
    accessTokenExpiresAt: v.accessTokenExpiresAt,
    refreshTokenExpiresAt: v.refreshTokenExpiresAt,
  }
}

async function probe() {
  // Single-flight: avoid stacking probes when the user mashes hover or
  // the interval fires mid-fetch.
  if (inflight) return
  inflight = true

  // Don't flicker to "checking" on every refresh — only show the
  // checking state on the very first probe (or after a disconnect).
  if (!details.value) status.value = 'checking'

  try {
    const peek = peekOAuthSession()
    const nowSec = Math.round(Date.now() / 1000)
    const accessLive = !!peek.accessToken && !!peek.accessTokenExpiresAt && peek.accessTokenExpiresAt > nowSec + 10
    const refreshLive = !!peek.refreshTokenExpiresAt && peek.refreshTokenExpiresAt > nowSec + 10
    hasOAuthToken.value = accessLive || refreshLive

    // ── OAuth path (fast, browser-native) ────────────────────
    // If the user has live OAuth creds in the cookie, hit Snowflake
    // directly. snowflakeAuth() will refresh the access token inline
    // when it's stale — safe here because refreshLive is true so it
    // won't hit its logout branch.
    if (hasOAuthToken.value) {
      const response: any = await useSnowflake({ query: STATUS_QUERY })
      if (response?.status === 'ERROR' || !response?.data?.length) {
        status.value = 'disconnected'
        errorMessage.value = response?.error || 'Snowflake returned no rows.'
        useGlobalsStore().setSnowflakeSession(null)
        lastCheckedAt.value = new Date()
        return
      }
      const row = response.data[0] || {}
      applySession(row)
      // CURRENT_AVAILABLE_ROLES() comes back as a JSON array string —
      // parse and push to the store. Done here (not inside applySession)
      // because the backend path returns a pre-parsed list, so applySession
      // stays free of "where did this come from" branching.
      applyRolesFromJson(row.availableRoles)
      lastCheckedAt.value = new Date()
      return
    }

    // ── Server-side path (all auth modes) ────────────────────
    // No OAuth tokens in the browser — fall through to the backend
    // probe. As of the unified user_snowflake_session helper, the
    // backend handles ALL FOUR auth modes (oauth, pat, password,
    // keypair). For OAuth specifically, it mints a fresh access
    // token from the user's stored refresh token via oauth_token_flow,
    // so the probe works even when the browser's cookie / localStorage
    // are missing or stale (fresh signup race, multi-tab state,
    // cookie size cap, etc.). This is a ~1s GCP roundtrip (vs ~100ms
    // for the browser path) but only fires on mount + every 60s.
    const sessionResp: any = await useCloudFunction('app_snowflake_session', {})

    if (!sessionResp?.ok || !sessionResp?.session) {
      status.value = 'disconnected'
      errorMessage.value = sessionResp?.error || 'Snowflake session probe failed.'
      useGlobalsStore().setSnowflakeSession(null)
      lastCheckedAt.value = new Date()
      return
    }

    applySession(sessionResp.session)
    // Backend pre-parses CURRENT_AVAILABLE_ROLES() into a string[] —
    // direct push, no JSON parse needed.
    if (Array.isArray(sessionResp.availableRoles)) {
      useGlobalsStore().setAvailableSnowflakeRoles(sessionResp.availableRoles)
    }
    lastCheckedAt.value = new Date()
  } catch (e: any) {
    status.value = 'disconnected'
    errorMessage.value = e?.message || String(e)
    useGlobalsStore().setSnowflakeSession(null)
    lastCheckedAt.value = new Date()
  } finally {
    inflight = false
  }
}

/**
 * Parse CURRENT_AVAILABLE_ROLES()'s JSON-string output into a real
 * array and push it to the Pinia store. Used by the OAuth (browser)
 * probe path; the backend probe pre-parses on the server.
 */
function applyRolesFromJson(raw: unknown) {
  if (!raw) return
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (Array.isArray(parsed)) {
      useGlobalsStore().setAvailableSnowflakeRoles(parsed.map(String))
    }
  } catch (e) {
    console.warn('Could not parse CURRENT_AVAILABLE_ROLES output:', e)
  }
}

function applySession(row: Record<string, unknown>) {
  const session = {
    user: stringify(row.user),
    role: stringify(row.role),
    warehouse: stringify(row.warehouse),
    database: stringify(row.database),
    schema: stringify(row.schema),
    region: stringify(row.region),
    account: stringify(row.account),
    version: stringify(row.version),
    sessionId: stringify(row.sessionId),
  }
  details.value = session
  // Mirror to the Pinia globals store so other surfaces (header role
  // line under the user's name, future role-aware dashboards, audit
  // overlays, etc.) share a single source of truth for "what's the
  // user's live Snowflake session?". The store is the canonical
  // location; the local `details` ref above just keeps render
  // bookkeeping local to this component.
  useGlobalsStore().setSnowflakeSession(session)
  status.value = 'connected'
  errorMessage.value = null
}

function onHover() {
  hovered.value = true
  // If the last check was more than 10s ago, kick a fresh probe so a
  // user mousing in to inspect doesn't see stale data. Cheap; the
  // single-flight guard prevents a flood.
  if (!lastCheckedAt.value || Date.now() - lastCheckedAt.value.getTime() > 10_000) {
    probe()
  }
}

function stringify(v: unknown): string {
  if (v == null) return ''
  return String(v)
}

function formatRelative(d: Date): string {
  const diff = Math.round((Date.now() - d.getTime()) / 1000)
  if (diff < 5) return 'just now'
  if (diff < 60) return `${diff}s ago`
  const min = Math.round(diff / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  return `${hr}h ago`
}

onMounted(() => {
  probe()
  intervalId = setInterval(probe, REFRESH_INTERVAL_MS)
})

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId)
})

// 💬 Re-probe immediately whenever the user switches their active
// Snowflake role. The role write goes to Firestore → Pinia computed
// updates → this watcher fires → fresh probe. Keeps the chip + hover
// card in sync within ~1s of a switch instead of waiting for the
// 60s background tick. Skip on the very first run (the role is
// already covered by the onMounted probe above).
const { activeSnowflakeRole } = storeToRefs(useGlobalsStore())
watch(activeSnowflakeRole, (next, prev) => {
  if (next === prev) return
  probe()
})
</script>
